import { normalizeLicensePlateForStorage } from "@/lib/licensePlate";
import type { CompanionFieldMode } from "@/lib/companionFields";
import type { BookingMessages } from "@/i18n/types";
import { de } from "@/i18n/de";
import type { PublicArrivalTypeOption } from "@/types";

export type VehicleFieldKey =
  | "license_plate"
  | "vehicle_length_m"
  | "vehicle_width_m"
  | "vehicle_height_m"
  | "vehicle_weight_kg"
  | "tent_size";

export type VehicleFieldsConfig = Record<VehicleFieldKey, CompanionFieldMode>;

export type VehicleFieldTextConfig = Partial<Record<VehicleFieldKey, string>>;

export type VehicleFieldFullConfig = {
  modes: VehicleFieldsConfig;
  tooltips: VehicleFieldTextConfig;
  max_values: VehicleFieldTextConfig;
  max_messages: VehicleFieldTextConfig;
};

export const NUMERIC_VEHICLE_FIELD_KEYS: VehicleFieldKey[] = [
  "vehicle_length_m",
  "vehicle_width_m",
  "vehicle_height_m",
  "vehicle_weight_kg",
];

export type VehicleFormData = Record<VehicleFieldKey, string>;

export const VEHICLE_FIELD_KEYS: VehicleFieldKey[] = [
  "license_plate",
  "vehicle_length_m",
  "vehicle_width_m",
  "vehicle_height_m",
  "vehicle_weight_kg",
  "tent_size",
];

export const VEHICLE_FIELD_LABELS: Record<VehicleFieldKey, string> = getVehicleFieldLabels(de);

export function getVehicleFieldLabels(
  messages: BookingMessages,
): Record<VehicleFieldKey, string> {
  const labels = messages.vehicleFields;
  return {
    license_plate: labels.license_plate,
    vehicle_length_m: labels.vehicle_length_m,
    vehicle_width_m: labels.vehicle_width_m,
    vehicle_height_m: labels.vehicle_height_m,
    vehicle_weight_kg: labels.vehicle_weight_kg,
    tent_size: labels.tent_size,
  };
}

export function vehicleFieldUnit(key: VehicleFieldKey): string | null {
  if (key === "vehicle_weight_kg") return "kg";
  if (key === "vehicle_length_m" || key === "vehicle_width_m" || key === "vehicle_height_m") {
    return "m";
  }
  return null;
}

export const EMPTY_VEHICLE_FORM: VehicleFormData = {
  license_plate: "",
  vehicle_length_m: "",
  vehicle_width_m: "",
  vehicle_height_m: "",
  vehicle_weight_kg: "",
  tent_size: "",
};

const OFF_VEHICLE_CONFIG: VehicleFieldsConfig = {
  license_plate: "off",
  vehicle_length_m: "off",
  vehicle_width_m: "off",
  vehicle_height_m: "off",
  vehicle_weight_kg: "off",
  tent_size: "off",
};

const EMPTY_VEHICLE_FIELD_FULL_CONFIG: VehicleFieldFullConfig = {
  modes: OFF_VEHICLE_CONFIG,
  tooltips: {},
  max_values: {},
  max_messages: {},
};

export function vehicleFieldsConfigFor(
  arrivalType: string,
  options: PublicArrivalTypeOption[],
): VehicleFieldsConfig {
  return vehicleFieldFullConfigFor(arrivalType, options).modes;
}

export function vehicleFieldFullConfigFor(
  arrivalType: string,
  options: PublicArrivalTypeOption[],
): VehicleFieldFullConfig {
  const option = options.find((t) => t.value === arrivalType);
  if (!option) return { ...EMPTY_VEHICLE_FIELD_FULL_CONFIG };
  return {
    modes: {
      ...OFF_VEHICLE_CONFIG,
      ...(option.vehicle_fields as VehicleFieldsConfig | undefined),
    },
    tooltips: { ...(option.vehicle_field_tooltips ?? {}) },
    max_values: { ...(option.vehicle_field_max_values ?? {}) },
    max_messages: { ...(option.vehicle_field_max_messages ?? {}) },
  };
}

export function vehicleFieldTooltip(
  config: VehicleFieldFullConfig,
  key: VehicleFieldKey,
): string | null {
  const custom = config.tooltips[key]?.trim();
  return custom || null;
}

function parseMaxValue(config: VehicleFieldFullConfig, key: VehicleFieldKey): number | null {
  const raw = config.max_values[key]?.trim();
  if (!raw) return null;
  const num = parseVehicleDecimal(raw);
  return num;
}

export function vehicleFieldMaxExceededMessage(
  config: VehicleFieldFullConfig,
  key: VehicleFieldKey,
  value: string,
): string | null {
  if (!NUMERIC_VEHICLE_FIELD_KEYS.includes(key)) return null;
  const num = parseVehicleDecimal(value);
  if (num === null) return null;
  const configuredMax = parseMaxValue(config, key);
  if (configuredMax === null || num <= configuredMax) return null;
  const custom = config.max_messages[key]?.trim();
  if (custom) return custom;
  const unit = vehicleFieldUnit(key);
  const maxLabel = unit ? `${configuredMax} ${unit}` : String(configuredMax);
  return `${VEHICLE_FIELD_LABELS[key]} darf höchstens ${maxLabel} betragen.`;
}

function resolveVehicleModes(
  config: VehicleFieldsConfig | VehicleFieldFullConfig,
): VehicleFieldsConfig {
  return "modes" in config ? config.modes : config;
}

export function activeVehicleFields(
  config: VehicleFieldsConfig | VehicleFieldFullConfig,
): VehicleFieldKey[] {
  const modes = resolveVehicleModes(config);
  return VEHICLE_FIELD_KEYS.filter((k) => modes[k] !== "off");
}

export function parseVehicleDecimal(value: string): number | null {
  const normalized = value.trim().replace(",", ".");
  if (!normalized) return null;
  const num = Number.parseFloat(normalized);
  if (!Number.isFinite(num) || num <= 0) return null;
  return num;
}

export function vehicleLengthMFromForm(form: VehicleFormData): number | null {
  const num = parseVehicleDecimal(form.vehicle_length_m);
  if (num === null || num > 25) return null;
  return num;
}

export function validateVehicleForm(
  form: VehicleFormData,
  config: VehicleFieldsConfig | VehicleFieldFullConfig,
): string | null {
  const modes = "modes" in config ? config.modes : config;
  const fullConfig: VehicleFieldFullConfig =
    "modes" in config
      ? config
      : {
          modes: config,
          tooltips: {},
          max_values: {},
          max_messages: {},
        };

  for (const key of VEHICLE_FIELD_KEYS) {
    if (modes[key] !== "required") continue;
    if (!form[key].trim()) {
      return `${VEHICLE_FIELD_LABELS[key]} ist erforderlich.`;
    }
    if (
      (key === "vehicle_length_m" ||
        key === "vehicle_width_m" ||
        key === "vehicle_height_m") &&
      parseVehicleDecimal(form[key]) === null
    ) {
      return `${VEHICLE_FIELD_LABELS[key]} ist ungültig.`;
    }
    if (key === "vehicle_weight_kg" && parseVehicleDecimal(form[key]) === null) {
      return `${VEHICLE_FIELD_LABELS[key]} ist ungültig.`;
    }
    const maxMessage = vehicleFieldMaxExceededMessage(fullConfig, key, form[key]);
    if (maxMessage) return maxMessage;
  }

  for (const key of NUMERIC_VEHICLE_FIELD_KEYS) {
    if (modes[key] === "off" || !form[key].trim()) continue;
    const maxMessage = vehicleFieldMaxExceededMessage(fullConfig, key, form[key]);
    if (maxMessage) return maxMessage;
  }

  return null;
}

export function vehicleFormReady(
  form: VehicleFormData,
  config: VehicleFieldsConfig | VehicleFieldFullConfig,
): boolean {
  return validateVehicleForm(form, config) === null;
}

export function buildVehiclePayload(
  form: VehicleFormData,
  config: VehicleFieldsConfig | VehicleFieldFullConfig,
): Record<string, string | number | null> {
  const modes = resolveVehicleModes(config);
  const payload: Record<string, string | number | null> = {};
  for (const key of VEHICLE_FIELD_KEYS) {
    if (modes[key] === "off") continue;
    const raw = form[key].trim();
    if (!raw) {
      payload[key] = null;
      continue;
    }
    if (
      key === "vehicle_length_m" ||
      key === "vehicle_width_m" ||
      key === "vehicle_height_m" ||
      key === "vehicle_weight_kg"
    ) {
      payload[key] = parseVehicleDecimal(raw);
    } else if (key === "license_plate") {
      payload[key] = normalizeLicensePlateForStorage(raw);
    } else {
      payload[key] = raw;
    }
  }
  return payload;
}

export function vehicleSummaryLines(
  form: VehicleFormData,
  config: VehicleFieldsConfig | VehicleFieldFullConfig,
): { key: VehicleFieldKey; label: string; value: string }[] {
  const modes = resolveVehicleModes(config);
  const lines: { key: VehicleFieldKey; label: string; value: string }[] = [];
  for (const key of VEHICLE_FIELD_KEYS) {
    if (modes[key] === "off") continue;
    const value = form[key].trim();
    if (!value) continue;
    const unit = vehicleFieldUnit(key);
    lines.push({
      key,
      label: VEHICLE_FIELD_LABELS[key],
      value: unit && parseVehicleDecimal(value) !== null ? `${value} ${unit}` : value,
    });
  }
  return lines;
}

const VEHICLE_FILTER_KEYS = [
  "vehicle_length_m",
  "vehicle_width_m",
  "vehicle_height_m",
  "vehicle_weight_kg",
] as const;

export function vehicleDimensionsAffectAvailability(
  config: VehicleFieldsConfig | VehicleFieldFullConfig,
): boolean {
  const modes = resolveVehicleModes(config);
  return VEHICLE_FILTER_KEYS.some((key) => modes[key] !== "off");
}

export function vehicleFilterParamsFromForm(
  form: VehicleFormData,
  config?: VehicleFieldFullConfig,
): Partial<Record<(typeof VEHICLE_FILTER_KEYS)[number], string>> {
  const params: Partial<Record<(typeof VEHICLE_FILTER_KEYS)[number], string>> = {};
  for (const key of VEHICLE_FILTER_KEYS) {
    const num = parseVehicleDecimal(form[key]);
    if (num === null) continue;
    const configuredMax = config ? parseMaxValue(config, key) : null;
    const max = configuredMax ?? (key === "vehicle_weight_kg" ? 50000 : 25);
    if (num <= max) params[key] = String(num);
  }
  return params;
}

export function appendVehicleAvailabilityParams(
  params: URLSearchParams,
  filters: Partial<Record<(typeof VEHICLE_FILTER_KEYS)[number], string>>,
  arrivalType?: string,
): void {
  if (arrivalType) {
    params.set("arrival_type", arrivalType);
  }
  for (const [key, value] of Object.entries(filters)) {
    if (value) params.set(key, value);
  }
}
